import { useMemo, useState } from 'react';
import { Chess, type Square } from 'chess.js';
import { Board } from '../board/Board';
import { severityLabel } from '../../lib/coaching/explain';
import { normalizeUci } from '../../lib/utils/chessHelpers';
import type { KeyMoment } from '../../types';

export interface MomentExplorerProps {
  moment: KeyMoment;
  index: number;
}

function buildVariationFens(fenBefore: string, lineSan: string[]): string[] {
  const chess = new Chess(fenBefore);
  const fens = [fenBefore];
  for (const san of lineSan) {
    try {
      chess.move(san);
      fens.push(chess.fen());
    } catch {
      break;
    }
  }
  return fens;
}

/** Rejoue : position de départ → coup joué (la « gaffe ») → réponse adverse
 * qui la punit (jusqu'à 4 coups de ligne indicative). Contrairement à
 * `buildVariationFens` (qui montre ce qu'il fallait jouer), cette séquence
 * montre ce qui arrive vraiment si le coup joué reste sur l'échiquier. */
function buildPunishmentFens(fenBefore: string, playedSan: string, replyLineSan: string[]): string[] {
  const chess = new Chess(fenBefore);
  const fens = [fenBefore];
  try {
    chess.move(playedSan);
    fens.push(chess.fen());
  } catch {
    return fens; // ne devrait pas arriver : playedSan vient de la partie réelle
  }
  for (const san of replyLineSan) {
    try {
      chess.move(san);
      fens.push(chess.fen());
    } catch {
      break;
    }
  }
  return fens;
}

const THEME_LABELS: Record<string, string> = {
  pièce_en_prise: 'Pièce en prise',
  mat_manqué: 'Mat manqué',
  mat_évitable: 'Mat évitable',
  fourchette: 'Fourchette',
  clouage_exploité: 'Clouage exploité',
  sécurité_du_roi: 'Sécurité du roi',
  perte_matérielle: 'Perte matérielle',
  coup_plus_précis_disponible: 'Coup plus précis disponible',
};

export function MomentExplorer({ moment, index }: MomentExplorerProps) {
  const [phase, setPhase] = useState<'guessing' | 'revealed'>('guessing');
  const [feedback, setFeedback] = useState<string | null>(null);
  const [attempts, setAttempts] = useState(0);
  const [variationIndex, setVariationIndex] = useState(0);
  const [punishmentIndex, setPunishmentIndex] = useState(0);

  const variationFens = useMemo(
    () => buildVariationFens(moment.fenBefore, moment.bestLineSan),
    [moment.fenBefore, moment.bestLineSan],
  );

  const punishmentFens = useMemo(
    () =>
      moment.punishment
        ? buildPunishmentFens(moment.fenBefore, moment.playedSan, moment.punishment.lineSan)
        : null,
    [moment.fenBefore, moment.playedSan, moment.punishment],
  );

  function handleGuess(uci: string) {
    setAttempts((a) => a + 1);
    if (normalizeUci(uci) === normalizeUci(moment.bestUci)) {
      setFeedback('Bien vu ! C\'était le meilleur coup dans cette position.');
      setPhase('revealed');
      setVariationIndex(0);
      setPunishmentIndex(0);
    } else {
      const chess = new Chess(moment.fenBefore);
      let sanTried = uci;
      try {
        const move = chess.move({ from: uci.slice(0, 2) as Square, to: uci.slice(2, 4) as Square, promotion: uci.slice(4) || undefined });
        if (move) sanTried = move.san;
      } catch {
        // on garde le uci brut si la conversion échoue
      }
      setFeedback(`${sanTried} n'est pas le coup que je retiens ici. Essaie encore, ou regarde la solution.`);
    }
  }

  function reveal() {
    setFeedback(null);
    setPhase('revealed');
    setVariationIndex(0);
    setPunishmentIndex(0);
  }

  function retry() {
    setPhase('guessing');
    setFeedback(null);
    setAttempts(0);
  }

  return (
    <article className="moment-card">
      <header className="moment-header">
        <h3>
          Moment {index + 1} — {severityLabel(moment.severity)} au coup {moment.ply}
        </h3>
        <div className="theme-badges">
          {moment.themes.map((t) => (
            <span key={t} className="theme-badge">
              {THEME_LABELS[t] ?? t}
            </span>
          ))}
        </div>
      </header>

      <p className="muted">
        Coup joué : <strong>{moment.playedSan}</strong> · perte estimée ≈{' '}
        {(moment.lossCp / 100).toFixed(1)} pion{moment.lossCp >= 150 ? 's' : ''}
      </p>

      {phase === 'guessing' ? (
        <>
          <p>À toi de chercher : quel meilleur coup aurais-tu pu jouer à la place de {moment.playedSan} ?</p>
          <Board fen={moment.fenBefore} orientation={moment.playerColor} playableColor={moment.playerColor} onUserMove={handleGuess} boardId={`moment-${moment.id}`} />
          {feedback && (
            <p className="feedback" role="status">
              {feedback}
            </p>
          )}
          <div className="actions">
            <button type="button" className="link-btn" onClick={reveal}>
              {attempts > 0 ? 'Voir la solution' : 'Je ne vois pas, montre-moi la solution'}
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="feedback success" role="status">
            {feedback ?? `Meilleur coup : ${moment.bestSan}.`}
          </p>
          <Board
            fen={variationFens[variationIndex]}
            orientation={moment.playerColor}
            lastMoveSquares={
              variationIndex > 0
                ? { from: moment.bestUci.slice(0, 2), to: moment.bestUci.slice(2, 4) }
                : null
            }
            boardId={`moment-variation-${moment.id}`}
          />
          <div className="nav-controls">
            <button type="button" onClick={() => setVariationIndex((i) => Math.max(0, i - 1))} disabled={variationIndex === 0}>
              ◀
            </button>
            <span className="muted">
              {variationIndex} / {variationFens.length - 1}
            </span>
            <button
              type="button"
              onClick={() => setVariationIndex((i) => Math.min(variationFens.length - 1, i + 1))}
              disabled={variationIndex === variationFens.length - 1}
            >
              ▶
            </button>
          </div>

          <section className="punishment-block">
            <h4>La punition</h4>
            {punishmentFens ? (
              <>
                <Board
                  fen={punishmentFens[punishmentIndex]}
                  orientation={moment.playerColor}
                  lastMoveSquares={
                    punishmentIndex > 0
                      ? punishmentIndex === 1
                        ? { from: moment.playedUci.slice(0, 2), to: moment.playedUci.slice(2, 4) }
                        : { from: moment.punishment!.replyUci.slice(0, 2), to: moment.punishment!.replyUci.slice(2, 4) }
                      : null
                  }
                  highlightSquares={
                    punishmentIndex >= 2 && moment.punishment!.captured ? [moment.punishment!.captured.square] : []
                  }
                  boardId={`moment-punishment-${moment.id}`}
                />
                <div className="nav-controls">
                  <button
                    type="button"
                    onClick={() => setPunishmentIndex((i) => Math.max(0, i - 1))}
                    disabled={punishmentIndex === 0}
                  >
                    ◀
                  </button>
                  <span className="muted">
                    Réponse {punishmentIndex} / {punishmentFens.length - 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => setPunishmentIndex((i) => Math.min(punishmentFens.length - 1, i + 1))}
                    disabled={punishmentIndex === punishmentFens.length - 1}
                  >
                    ▶
                  </button>
                </div>
                <p className="muted small">
                  {punishmentIndex === 0 && 'Position avant ton coup.'}
                  {punishmentIndex === 1 && `Après ${moment.playedSan} : à l'adversaire de jouer.`}
                  {punishmentIndex >= 2 && moment.explanation.whatOpponentGets}
                </p>
              </>
            ) : (
              <p className="muted small">Relance l'analyse pour voir la punition détaillée.</p>
            )}
          </section>

          <dl className="explanation">
            <dt>Ce que ton coup permettait à l'adversaire</dt>
            <dd>{moment.explanation.whatOpponentGets}</dd>
            <dt>Meilleure option</dt>
            <dd>{moment.explanation.betterOption}</dd>
            <dt>Le réflexe à retenir</dt>
            <dd>{moment.explanation.reflex}</dd>
          </dl>
          <div className="actions">
            <button type="button" className="link-btn" onClick={retry}>
              ← Revenir à la partie / réessayer
            </button>
          </div>
        </>
      )}
    </article>
  );
}
